/**
 * Outfitter Manager
 *
 * Manages outfitter shops where players BUY gear, tackle, boats, etc.
 * Follows the Manager singleton pattern from architecture.
 */

import { useGameStore } from '@/state';
import { eventBus } from '@/services/EventBus';
import { OutfitterDefinition, ShopPurchaseRecord, GameEvent } from '@/types';
import { equipmentManager } from '@/systems/equipment/EquipmentManager';

export class OutfitterManager {
  private outfitters: Map<string, OutfitterDefinition> = new Map();
  private stockOverrides: Map<string, Map<string, number>> = new Map();
  private restockIntervalId: number | null = null;

  /**
   * Register an outfitter definition
   */
  registerOutfitter(definition: OutfitterDefinition): void {
    this.outfitters.set(definition.id, definition);
  }

  /**
   * Register multiple outfitters
   */
  registerOutfitters(definitions: OutfitterDefinition[]): void {
    definitions.forEach((def) => this.registerOutfitter(def));
  }

  /**
   * Get an outfitter definition by ID
   */
  getOutfitter(outfitterId: string): OutfitterDefinition | undefined {
    return this.outfitters.get(outfitterId);
  }

  /**
   * Get all registered outfitters
   */
  getAllOutfitters(): OutfitterDefinition[] {
    return Array.from(this.outfitters.values());
  }

  /**
   * Get outfitters available in a specific zone
   */
  getOutfittersInZone(_zoneId: string): OutfitterDefinition[] {
    // Would need zone definitions to check which outfitters are in zone
    // For now, return all outfitters
    return this.getAllOutfitters();
  }

  /**
   * Check if player has unlocked an outfitter
   */
  isOutfitterUnlocked(outfitterId: string): boolean {
    const state = useGameStore.getState();
    return state.playerState.shops.unlockedOutfitters.includes(outfitterId);
  }

  /**
   * Check if player meets unlock requirements for an outfitter
   */
  canUnlockOutfitter(outfitterId: string): boolean {
    const outfitter = this.getOutfitter(outfitterId);
    if (!outfitter) return false;

    if (outfitter.unlockedByDefault) return true;
    if (this.isOutfitterUnlocked(outfitterId)) return true;

    const state = useGameStore.getState();
    const requirements = outfitter.unlockRequirements;

    if (!requirements) return true;

    // Check level requirement
    if (requirements.minLevel !== undefined) {
      // Future: Add player level system
      // if (state.playerState.level < requirements.minLevel) return false;
    }

    // Check currency requirement
    if (requirements.minCurrency !== undefined) {
      if (state.playerState.currency < requirements.minCurrency) return false;
    }

    // Check completed quests
    if (requirements.completedQuests) {
      const completed = state.playerState.quests.completedQuests;
      if (!requirements.completedQuests.every((q) => completed.includes(q))) {
        return false;
      }
    }

    // Check fish caught milestone
    if (requirements.totalFishCaught !== undefined) {
      if (state.playerState.stats.totalFishCaught < requirements.totalFishCaught) {
        return false;
      }
    }

    // Check fish sold milestone
    if (requirements.totalFishSold !== undefined) {
      if (state.playerState.stats.totalFishSold < requirements.totalFishSold) {
        return false;
      }
    }

    // Check buddy count
    if (requirements.buddyCount !== undefined) {
      const hiredBuddies = state.playerState.fishingBuddies.filter((b) => b.hired).length;
      if (hiredBuddies < requirements.buddyCount) return false;
    }

    // Check zone unlocked
    if (requirements.zoneUnlocked) {
      if (!state.playerState.unlockedZones.includes(requirements.zoneUnlocked)) {
        return false;
      }
    }

    return true;
  }

  /**
   * Unlock an outfitter
   */
  unlockOutfitter(outfitterId: string): boolean {
    if (!this.canUnlockOutfitter(outfitterId)) {
      console.warn(`Cannot unlock outfitter ${outfitterId} - requirements not met`);
      return false;
    }

    if (this.isOutfitterUnlocked(outfitterId)) {
      console.warn(`Outfitter ${outfitterId} already unlocked`);
      return false;
    }

    useGameStore.setState((draft) => {
      draft.playerState.shops.unlockedOutfitters.push(outfitterId);
    });

    eventBus.emit(GameEvent.SHOP_UNLOCKED, {
      shopId: outfitterId,
      shopType: 'outfitter',
    });

    return true;
  }

  /**
   * Get the current stock for an item, checking runtime overrides first, then definition defaults.
   * Returns -1 for infinite stock.
   */
  getItemStock(outfitterId: string, equipmentId: string): number {
    const outfitter = this.getOutfitter(outfitterId);
    if (!outfitter) return 0;

    const item = outfitter.inventory.find((i) => i.equipmentId === equipmentId);
    if (!item) return 0;

    // Check runtime stock overrides first
    const shopOverrides = this.stockOverrides.get(outfitterId);
    if (shopOverrides?.has(equipmentId)) {
      return shopOverrides.get(equipmentId)!;
    }

    // Fall back to definition default
    return item.stock;
  }

  /**
   * Check if an item is in stock
   */
  isItemInStock(outfitterId: string, equipmentId: string): boolean {
    const stock = this.getItemStock(outfitterId, equipmentId);
    // -1 = infinite stock
    return stock === -1 || stock > 0;
  }

  /**
   * Get item price with discount applied
   */
  getItemPrice(outfitterId: string, equipmentId: string): number | null {
    const outfitter = this.getOutfitter(outfitterId);
    if (!outfitter) return null;

    const item = outfitter.inventory.find((i) => i.equipmentId === equipmentId);
    if (!item) return null;

    const basePrice = item.price;
    const discount = item.discount || 0;

    return Math.floor(basePrice * (1 - discount));
  }

  /**
   * Purchase an item from an outfitter.
   * Validates outfitter-specific concerns (shop unlocked, item in stock, discounts)
   * then delegates equipment ownership, equipping, and saving to EquipmentManager.
   */
  purchaseItem(outfitterId: string, equipmentId: string): boolean {
    // 1. Validate outfitter exists and is unlocked
    const outfitter = this.getOutfitter(outfitterId);
    if (!outfitter) {
      console.warn(`Outfitter ${outfitterId} not found`);
      return false;
    }

    if (!this.isOutfitterUnlocked(outfitterId)) {
      console.warn(`Outfitter ${outfitterId} not unlocked`);
      return false;
    }

    // 2. Validate item exists and is in stock
    const item = outfitter.inventory.find((i) => i.equipmentId === equipmentId);
    if (!item) {
      console.warn(`Item ${equipmentId} not found in outfitter ${outfitterId}`);
      return false;
    }

    if (!this.isItemInStock(outfitterId, equipmentId)) {
      console.warn(`Item ${equipmentId} out of stock`);
      return false;
    }

    // 3. Get discounted price
    const price = this.getItemPrice(outfitterId, equipmentId);
    if (price === null) return false;

    // 4. Delegate to EquipmentManager (handles currency, ownership, equip, save, EQUIPMENT_PURCHASED event)
    const success = equipmentManager.purchaseEquipment(equipmentId, price);
    if (!success) return false;

    // 5. Update stock in runtime tracking (NOT on the definition)
    const currentStock = this.getItemStock(outfitterId, equipmentId);
    if (currentStock > 0) {
      if (!this.stockOverrides.has(outfitterId)) {
        this.stockOverrides.set(outfitterId, new Map());
      }
      this.stockOverrides.get(outfitterId)!.set(equipmentId, currentStock - 1);
    }

    // 6. Record purchase history and visited shops
    useGameStore.setState((draft) => {
      const purchaseRecord: ShopPurchaseRecord = {
        shopId: outfitterId,
        equipmentId,
        price,
        purchasedAt: Date.now(),
      };

      if (!draft.playerState.shops.purchaseHistory) {
        draft.playerState.shops.purchaseHistory = [];
      }
      draft.playerState.shops.purchaseHistory.push(purchaseRecord);

      draft.playerState.shops.visitedShops[outfitterId] = Date.now();
    });

    // 7. Emit CURRENCY_SPENT event (EquipmentManager already emits EQUIPMENT_PURCHASED)
    eventBus.emit(GameEvent.CURRENCY_SPENT, {
      amount: price,
      reason: 'equipment_purchase',
    });

    return true;
  }

  /**
   * Restock an outfitter (for shops with restock intervals)
   */
  restockOutfitter(outfitterId: string): void {
    const outfitter = this.getOutfitter(outfitterId);
    if (!outfitter) return;

    if (outfitter.restockInterval === undefined || outfitter.restockInterval === 0) {
      // No restocking needed
      return;
    }

    const now = Date.now();
    const lastRestock = outfitter.restockDate || 0;
    const restockIntervalMs = outfitter.restockInterval * 60 * 60 * 1000; // Convert hours to ms

    if (now - lastRestock >= restockIntervalMs) {
      // Clear stock overrides to restore definition defaults
      this.stockOverrides.delete(outfitterId);
      outfitter.restockDate = now;

      eventBus.emit(GameEvent.SHOP_RESTOCKED, {
        shopId: outfitterId,
      });
    }
  }

  /**
   * Check for restocks across all outfitters
   */
  checkRestocks(): void {
    this.getAllOutfitters().forEach((outfitter) => {
      this.restockOutfitter(outfitter.id);
    });
  }

  /**
   * Visit an outfitter (update last visited timestamp)
   */
  visitOutfitter(outfitterId: string): void {
    useGameStore.setState((draft) => {
      draft.playerState.shops.visitedShops[outfitterId] = Date.now();
    });
  }

  /**
   * Initialize manager
   */
  initialize(): void {
    // Set up periodic restock checks
    this.restockIntervalId = window.setInterval(() => {
      this.checkRestocks();
    }, 60000); // Check every minute

    console.log('[OutfitterManager] Initialized');
  }

  /**
   * Cleanup
   */
  cleanup(): void {
    // Clear intervals
    if (this.restockIntervalId !== null) {
      window.clearInterval(this.restockIntervalId);
      this.restockIntervalId = null;
    }
  }
}

// Singleton instance
export const outfitterManager = new OutfitterManager();
